/**
 * Rounds a number to the specified precision, avoiding IEEE 754 floating point issues.
 * Uses Number.EPSILON to correct for floating point representation errors.
 *
 * @param {number} value - The number to round.
 * @param {number} precision - The number of decimal places.
 * @returns {number} The rounded number.
 */
export const roundToPrecision = (value: number, precision: number): number => {
  const multiplier = Math.pow(10, precision);
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
};

/**
 * Formats a number to a string with two decimal places, a comma as the decimal separator,
 * and spaces as thousand separators.
 *
 * ## Example Usage:
 * ```tsx
 * import { formatValue } from '@/utils/formatters';
 *
 * console.log(formatValue(1234.56)); // Output: "1 234,56"
 * console.log(formatValue(1000000)); // Output: "1 000 000,00"
 * console.log(formatValue(45.1));    // Output: "45,10"
 * ```
 *
 * @param {number} value - The number to format.
 * @returns {string} The formatted string with spaces as thousand separators and a comma as the decimal separator.
 */
export const formatValue = (value: number): string => {
  const rounded = roundToPrecision(value, 2);
  const fixed = rounded.toFixed(2);
  const parts = fixed.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join(',');
};